import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import './App.css';
import { isAuthenticated } from './services/auth';

import { useMaintenanceMode } from './hooks/useMaintenanceMode';
import usePageTracking from './hooks/usePageTracking';
import MaintenancePage from './pages/MaintenancePage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Layouts
import HomePage from './components/HomePage';

// Pages
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Contact from './pages/Contact';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import Downloads from './pages/Downloads';
import Documentation from './pages/Documentation';
// import Tutorials from './pages/Tutorials'; // Commented out - keeping code for future use
import Privacy from './pages/Privacy';
import NotFoundPage from './pages/NotFoundPage';

// Account Pages
import SubscriptionPage from './pages/account/SubscriptionPage';
import SettingsPage from './pages/account/SettingsPage';
import BillingHistoryPage from './pages/account/BillingHistoryPage';
// import ReferralPage from './pages/account/ReferralPage'; // Commented out - keeping code for future use
import ChangePasswordPage from './pages/account/ChangePasswordPage';
import LeaveReviewPage from './pages/account/LeaveReviewPage';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageTracker = () => {
  usePageTracking();
  return null;
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  return children;
};

function App() {
  const { isMaintenanceMode, hasBypass, isLoading, grantBypass } = useMaintenanceMode();
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(64);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.offsetHeight;
      setHeaderHeight(prev => (prev !== h ? h : prev));
    };

    // Measure immediately
    measure();

    // ResizeObserver for size changes
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    // Also observe each child individually for more reliable detection
    Array.from(el.children).forEach(child => resizeObserver.observe(child));

    // MutationObserver for child additions/removals (e.g. banner appearing/disappearing)
    const mutationObserver = new MutationObserver(() => {
      measure();
      // Re-observe new children
      Array.from(el.children).forEach(child => resizeObserver.observe(child));
    });
    mutationObserver.observe(el, { childList: true, subtree: true, attributes: true });

    // Fallback: poll briefly on mount to catch any missed changes
    const timers = [
      setTimeout(measure, 100),
      setTimeout(measure, 500),
    ];

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  // Show loading state while checking maintenance status
  if (isLoading) {
    return (
      <ThemeProvider>
        <AuthProvider>
          <div className="min-h-screen bg-blue-50 dark:bg-gray-900 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading...</p>
            </div>
          </div>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  // Show maintenance page if maintenance mode is enabled and user doesn't have bypass
  if (isMaintenanceMode && !hasBypass) {
    return (
      <ThemeProvider>
        <AuthProvider>
          <MaintenancePage onBypassGranted={grantBypass} />
        </AuthProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <PageTracker />
        <div ref={headerRef} className="fixed top-0 left-0 right-0 z-50">
          <Navbar />
        </div>
        <div className="App min-h-screen flex flex-col" style={{ paddingTop: headerHeight }}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/downloads" element={<Downloads />} />
            <Route path="/documentation" element={<Documentation />} />
            {/* <Route path="/tutorials" element={<Tutorials />} /> */} {/* Commented out - keeping code for future use */}
            <Route path="/privacy" element={<Privacy />} />
            
            {/* Protected account routes */}
            <Route
              path="/account/profile"
              element={<Navigate to="/account/settings?tab=profile" replace />}
            />
            <Route 
              path="/account/subscription" 
              element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/account/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/account/billing-history" 
              element={
                <ProtectedRoute>
                  <BillingHistoryPage />
                </ProtectedRoute>
              } 
            />

            {/* <Route
              path="/account/referrals"
              element={
                <ProtectedRoute>
                  <ReferralPage />
                </ProtectedRoute>
              }
            /> */} {/* Commented out - keeping code for future use */}
            
            <Route
              path="/account/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/account/review"
              element={
                <ProtectedRoute>
                  <LeaveReviewPage />
                </ProtectedRoute>
              }
            />

            {/* Fallback for unmatched routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer />
        </div>
      </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;